import { useMemo } from 'react';

import getRuntimeAppConfig from '@/utils/runtimeAppConfig';

import useGetCompanyNumbers from '../setupProgress/queries/useGetCompanyNumbers';
import useGetCompanyUsers from '../setupProgress/queries/useGetCompanyUsers';
import useGetCompanyTrunks from '../queries/useGetCompanyTrunks';
import useGetCompanyAdminUsers from './queries/useGetCompanyAdminUsers';
import useGetCompanyApiUsers from './queries/useGetCompanyApiUsers';
import useGetCompanyTrsRegistrations from './queries/useGetCompanyTrsRegistrations';
import buildNotifications from './buildNotifications';

/**
 * (3153) The dashboard's alerts for one company. Numbers and users share the
 * setup progress bar's queries, and trunks the account summary's, so only
 * admins, API users and TRS registrations are extra requests.
 *
 * @param {object} params
 * @param {string} params.companyId
 * @param {object} [params.serviceHealth] - reported by the service sections
 * @param {object} [params.company] - the dashboard's /Company row
 * @param {object} [params.msAuth] - the company's Microsoft Teams app auth
 * @param {boolean} [params.showLegacySyncRetirement] - the company still
 *   needs to upgrade off legacy Microsoft sync
 */
const useNotifications = ({
    companyId,
    serviceHealth,
    company,
    msAuth,
    showLegacySyncRetirement = false,
}) => {
    const { data: numbers } = useGetCompanyNumbers(companyId);
    const { data: users } = useGetCompanyUsers(companyId);
    const { data: trunks } = useGetCompanyTrunks(companyId);
    const { data: admins } = useGetCompanyAdminUsers(companyId);
    const { data: apiUsers } = useGetCompanyApiUsers(companyId);
    const { data: trsRegistrations } = useGetCompanyTrsRegistrations(companyId);
    // Same date and fallback as SyncRetirementBanner.
    const legacySyncRetirementDate = showLegacySyncRetirement
        ? (getRuntimeAppConfig().legacySyncRetirementDate ?? 'October 3rd 2026')
        : null;

    return useMemo(
        () =>
            buildNotifications(
                {
                    numbers,
                    admins,
                    apiUsers,
                    tcapUsers: users?.tcapUsers,
                    trunks,
                    trsRegistrations,
                    serviceHealth,
                    company,
                    msAuth,
                    legacySyncRetirementDate,
                },
                Date.now(),
            ),
        [
            numbers,
            admins,
            apiUsers,
            users,
            trunks,
            trsRegistrations,
            serviceHealth,
            company,
            msAuth,
            legacySyncRetirementDate,
        ],
    );
};

export default useNotifications;
